import React, { createContext, useContext, useState, useEffect } from 'react';
import { message } from 'antd';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('authUser');

    if (token && userData) {
      // Proactive token expiry check — tokens expire after 24h
      const tokenCreatedAt = localStorage.getItem('authTokenCreatedAt');
      if (tokenCreatedAt) {
        const elapsed = Date.now() - parseInt(tokenCreatedAt, 10);
        const TWENTY_THREE_HOURS = 23 * 60 * 60 * 1000;
        if (elapsed > TWENTY_THREE_HOURS) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('authTokenCreatedAt');
          localStorage.removeItem('authUser');
          setLoading(false);
          return;
        }
      }

      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setIsAuthenticated(true);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('authToken');
        localStorage.removeItem('authTokenCreatedAt');
        localStorage.removeItem('authUser');
      }
    }
    setLoading(false);
  }, []);

  const login = async (credentials) => {
    try {
      const response = await api.post('/login', credentials);
      const data = response.data.data || response.data;
      if (data.requires_2fa) {
        return { requires_2fa: true, user_id: data.user_id };
      }
      const token = data.token;
      const userData = data.user || data;

      if (!token) {
        message.error('Authentication failed. No token received.');
        return false;
      }

      if (userData.user_type !== 'customer') {
        message.error(
          'Access denied. This portal is for customers only. Please use the appropriate portal (Admin, Provider, or Delivery app).',
        );
        return false;
      }

      localStorage.setItem('authToken', token);
      localStorage.setItem('authTokenCreatedAt', Date.now().toString());
      localStorage.setItem('authUser', JSON.stringify(userData));

      setUser(userData);
      setIsAuthenticated(true);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      return true;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Login failed';
      message.error(errorMessage);
      return false;
    }
  };

  const verify2fa = async (userId, code) => {
    try {
      const response = await api.post('/verify-2fa-login', { user_id: userId, code });
      const data = response.data.data || response.data;
      const token = data.token;
      const userData = data.user || data;

      if (!token) {
        message.error('Verification failed. No token received.');
        return false;
      }

      if (userData.user_type !== 'customer') {
        message.error('Access denied. This portal is for customers only.');
        return false;
      }

      localStorage.setItem('authToken', token);
      localStorage.setItem('authTokenCreatedAt', Date.now().toString());
      localStorage.setItem('authUser', JSON.stringify(userData));

      setUser(userData);
      setIsAuthenticated(true);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      message.success('Login successful!');
      return true;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Verification failed';
      message.error(errorMessage);
      return false;
    }
  };

  const googleLogin = async (payload) => {
    try {
      const response = await api.post('/firebase-google-signin', payload);
      const data = response.data.data || response.data;
      const token = data.token || data.user?.token;
      const userData = data.user || data;

      if (!token) {
        message.error('Authentication failed. No token received.');
        return false;
      }

      if (userData.user_type !== 'customer') {
        message.error('Access denied. This portal is for customers only.');
        return false;
      }

      localStorage.setItem('authToken', token);
      localStorage.setItem('authTokenCreatedAt', Date.now().toString());
      localStorage.setItem('authUser', JSON.stringify(userData));

      setUser(userData);
      setIsAuthenticated(true);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      return true;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Google sign-in failed';
      message.error(errorMessage);
      return false;
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/register', userData);
      const data = response.data.data || response.data;
      const token = data.token;
      const newUser = data.user || data;

      localStorage.setItem('authToken', token);
      localStorage.setItem('authTokenCreatedAt', Date.now().toString());
      localStorage.setItem('authUser', JSON.stringify(newUser));

      setUser(newUser);
      setIsAuthenticated(true);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      return true;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Registration failed';
      message.error(errorMessage);
      return false;
    }
  };

  const logout = (sessionExpired = false) => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authTokenCreatedAt');
    localStorage.removeItem('authUser');
    setUser(null);
    setIsAuthenticated(false);
    delete api.defaults.headers.common['Authorization'];
    if (sessionExpired) {
      window.location.href = '/login?session_expired=1';
    } else {
      window.location.href = '/login';
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('authUser', JSON.stringify(userData));
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, isAuthenticated, login, register, logout, updateUser, googleLogin, verify2fa }}
    >
      {children}
    </AuthContext.Provider>
  );
};
